import React, { Suspense, useCallback } from 'react';
import { FormProvider, SubmitHandler } from 'react-hook-form';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { openModal } from 'features/modal/modalSlice';
import SettingsModal from 'features/join/publisher/settings/SettingsModal';
import { useMediaDevices } from 'hooks/useMediaDevices';
import Header from 'features/join/publisher/components/Header';

import BlockedCameraPopper from 'features/join/publisher/components/poppers/BlockedCameraPopper';
import UnavailableCameraPopper from 'features/join/publisher/components/poppers/UnavailableCameraPopper';
import Icon from 'components/Icon';
import BlockedMicPopper from 'features/join/publisher/components/poppers/BlockedMicPopper';
import UnavailableMicPopper from 'features/join/publisher/components/poppers/UnavailableMicPopper';
import FormFooter from 'components/FormFooter';
import VUMeter from 'components/VUMeter';
import { useTranslation } from 'react-i18next';
import { JoinScreenInner, JoinScreenInnerWide } from 'features/join/styles';
import { Box, Button, Grid } from '@mui/material';
import Popper from 'components/Popper';
import VideoRenderer from 'components/VideoRenderer';
import { useBroadcastSetup } from 'utils/broadcast-setup';
import { TooltipIconTrigger } from 'components/TooltipIconTrigger';
import { useBroadcastSetupPreview } from 'utils/broadcast-setup/useBroadcastSetupPreview';
import { selectAwaitingRoomJoin } from 'features/room/roomSlice';
import AwaitingRoomJoin from 'features/join/shared/AwaitingRoomJoin';
import SettingControl from 'components/SettingControl';
import { Switch } from 'components/Switch';
import { usePermissions } from 'features/permissions/usePermissions';
import { PermissionTypes } from 'features/permissions/types';
import { useJoinMediaKeyboardShortcuts } from 'features/join/hooks/useJoinMediaKeyboardShortcuts';
import { useJoinActionKeyboardShortcut } from 'features/join/hooks/useJoinActionKeyboardShortcut';

export type MediaDeviceChangeHandler = (value: string) => void;

export type PreviewProps = {
  joinRoom: SubmitHandler<PreviewFormValues>;
};

export type PreviewFormValues = {
  videoinput: string;
  audiooutput: string;
  audioinput: string;
};

const JoinPreview = ({ joinRoom }: PreviewProps) => {
  const { t } = useTranslation(['join', 'common']);
  const appDispatch = useAppDispatch();

  const { micPermissions, camPermissions } = useMediaDevices();
  const { hasPermissions } = usePermissions();

  const allowVideoBroadcast = hasPermissions(PermissionTypes.broadcastVideo);
  const allowAudioBroadcast = hasPermissions(PermissionTypes.broadcastAudio);

  const { audio, video, onAudioToggle, onVideoToggle, videoStream, audioStream } =
    useBroadcastSetup();

  const formMethods = useBroadcastSetupPreview();
  const { handleSubmit } = formMethods;

  const awaitingRoomJoin = useAppSelector(selectAwaitingRoomJoin);

  useJoinMediaKeyboardShortcuts();

  const handleJoin = useCallback(() => {
    handleSubmit(joinRoom)();
  }, [handleSubmit, joinRoom]);

  useJoinActionKeyboardShortcut({
    onJoin: handleJoin,
    disabled: audio.processing || video.processing,
  });

  const openSettings = () => {
    appDispatch(openModal('publisherSettings'));
  };

  const renderCameraPopover = () =>
    camPermissions === 'denied' ? <BlockedCameraPopper /> : <UnavailableCameraPopper />;

  const renderMicPopover = () =>
    micPermissions === 'denied' ? <BlockedMicPopper /> : <UnavailableMicPopper />;

  const renderJoinButton = () => {
    if (awaitingRoomJoin) {
      return <AwaitingRoomJoin />;
    }

    return (
      <Button
        type="submit"
        variant="contained"
        fullWidth
        disabled={audio.processing || video.processing}
      >
        {t('publisher.btn_label_join')}
      </Button>
    );
  };

  return (
    <FormProvider {...formMethods}>
      <JoinScreenInnerWide>
        <Box sx={{ display: { xs: 'block', lg: 'none' } }}>
          <Header />
        </Box>
        <Grid
          container
          spacing={0}
          sx={{
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          {allowVideoBroadcast && (
            <Grid
              size={{
                xs: 12,
                lg: 7,
              }}
            >
              <Box
                sx={{
                  mb: { xs: 8, lg: 0 },
                  position: 'relative',
                  overflow: 'hidden',
                  borderRadius: 1,
                }}
              >
                <VideoRenderer mediaStream={videoStream} />
              </Box>
            </Grid>
          )}
          <Grid
            size={{
              xs: 12,
              lg: allowVideoBroadcast ? 5 : 12,
            }}
            sx={{
              flexShrink: 0,
            }}
          >
            <JoinScreenInner sx={{ ml: { lg: allowVideoBroadcast ? 10 : 'auto' } }}>
              <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
                <Header />
              </Box>
              <form onSubmit={handleSubmit(joinRoom)}>
                {allowVideoBroadcast && (
                  <Box
                    sx={{
                      mb: 8,
                    }}
                  >
                    <SettingControl
                      label={t('publisher.camera_control.label')}
                      control={
                        <Switch
                          onChange={(e) => onVideoToggle(e.target.checked)}
                          checked={video.active}
                          slotProps={{
                            input: {
                              'aria-label': video.active
                                ? t('publisher.camera_control.enabled_aria_label')
                                : t('publisher.camera_control.disabled_aria_label'),
                            },
                          }}
                          disabled={video.disabled || !allowVideoBroadcast}
                        />
                      }
                      infoIcon={
                        allowVideoBroadcast && camPermissions !== 'granted' ? (
                          <Popper
                            id={camPermissions === 'denied' ? 'blockedCamera' : 'unavailableCamera'}
                            placement="right"
                            content={renderCameraPopover()}
                          >
                            <TooltipIconTrigger>
                              <Icon name="warning" />
                            </TooltipIconTrigger>
                          </Popper>
                        ) : undefined
                      }
                    />
                  </Box>
                )}
                <div>
                  <SettingControl
                    label={t('publisher.mic_control.label')}
                    control={
                      <Switch
                        onChange={(e) => onAudioToggle(e.target.checked)}
                        checked={audio.active}
                        slotProps={{
                          input: {
                            'aria-label': audio.active
                              ? t('publisher.mic_control.enabled_aria_label')
                              : t('publisher.mic_control.disabled_aria_label'),
                          },
                        }}
                        disabled={audio.disabled || !allowAudioBroadcast}
                      />
                    }
                    infoIcon={
                      allowAudioBroadcast && micPermissions !== 'granted' ? (
                        <Popper
                          id={micPermissions === 'denied' ? 'blockedMic' : 'unavailableMic'}
                          placement="right"
                          content={renderMicPopover()}
                        >
                          <TooltipIconTrigger>
                            <Icon name="warning" />
                          </TooltipIconTrigger>
                        </Popper>
                      ) : undefined
                    }
                  />
                </div>
                <Box
                  sx={{
                    mt: 6,
                  }}
                >
                  <VUMeter
                    on={audio.active}
                    stream={audioStream}
                    label={t('common:vumeter.vumeter_label')}
                  />
                </Box>
                <Button
                  sx={{
                    '&&&': {
                      mt: 4,
                      pl: 0,
                    },
                  }}
                  startIcon={<Icon name="settings" />}
                  variant="text"
                  onClick={openSettings}
                >
                  {allowVideoBroadcast
                    ? t('publisher.btn_settings_label')
                    : t('publisher.btn_settings_audio_label')}
                </Button>
                <FormFooter>{renderJoinButton()}</FormFooter>
              </form>
            </JoinScreenInner>
          </Grid>
        </Grid>
      </JoinScreenInnerWide>
      <Suspense fallback={null}>
        <SettingsModal />
      </Suspense>
    </FormProvider>
  );
};

export default JoinPreview;
